import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { Close, FiberManualRecord, Stop, Send, Refresh, PlayArrow } from '@mui/icons-material';
import { FC, useState, useEffect, useRef } from 'react';

interface VideoCreateModalProps {
  open: boolean;
  onClose: () => void;
  questionText: string;
  questionId: number;
  isUploading?: boolean;
  onSubmit: (videoBlob: Blob, questionId: number) => Promise<void>;
}

type RecordingState = 'idle' | 'recording' | 'recorded' | 'viewing';

export const VideoCreateModal: FC<VideoCreateModalProps> = ({
  open,
  onClose,
  questionText,
  questionId,
  isUploading = false,
  onSubmit,
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [timeRemaining, setTimeRemaining] = useState(90);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaDevices, setMediaDevices] = useState<{
    cameras: MediaDeviceInfo[];
    microphones: MediaDeviceInfo[];
  }>({ cameras: [], microphones: [] });
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const progress = (timeRemaining / 90) * 100;
  const isLowTime = timeRemaining <= 15;
  const canStopRecording = recordingDuration >= 5;

  useEffect(() => {
    if (open) {
      getMediaDevices();
    }
    return () => {
      stopStream();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open]);

  const getMediaDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      const microphones = devices.filter((device) => device.kind === 'audioinput');

      setMediaDevices({ cameras, microphones });

      if (cameras.length > 0 && !selectedCamera) {
        setSelectedCamera(cameras[0].deviceId);
      }
      if (microphones.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(microphones[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting media devices:', error);
    }
  };

  const startStream = async () => {
    try {
      const videoConstraints = {
        deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
        width: { max: 1920 },
        height: { max: 1080 },
        frameRate: { max: 30 },
      };

      const constraints: MediaStreamConstraints = {
        video: videoConstraints,
        audio: selectedMicrophone ? { deviceId: { exact: selectedMicrophone } } : true,
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      await getMediaDevices();
    } catch (error) {
      console.error('Error starting stream:', error);
    }
  };

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const startRecording = async () => {
    if (!stream) {
      await startStream();
      return;
    }

    try {
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlob(blob);
        setRecordingState('viewing');
      };

      mediaRecorder.start();
      setRecordingState('recording');
      setTimeRemaining(90);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });

        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleRerecord = () => {
    setRecordingState('idle');
    setTimeRemaining(90);
    setRecordingDuration(0);
    setRecordedBlob(null);
    startStream();
  };

  const handleSubmit = async () => {
    if (recordedBlob) {
      await onSubmit(recordedBlob, questionId);
      if (!isUploading) {
        handleClose();
      }
    }
  };

  const handleClose = () => {
    if (isUploading) return;

    stopStream();
    setRecordingState('idle');
    setTimeRemaining(90);
    setRecordingDuration(0);
    setRecordedBlob(null);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onClose();
  };

  const handleCameraChange = (event: SelectChangeEvent) => {
    setSelectedCamera(event.target.value);
  };

  const handleMicrophoneChange = (event: SelectChangeEvent) => {
    setSelectedMicrophone(event.target.value);
  };

  useEffect(() => {
    if (selectedCamera || selectedMicrophone) {
      if (stream && recordingState === 'idle') {
        stopStream();
        startStream();
      }
    }
  }, [selectedCamera, selectedMicrophone]);

  useEffect(() => {
    if (open && recordingState === 'idle') {
      startStream();
    }
  }, [open]);

  // Auto-play recorded video when in viewing state
  useEffect(() => {
    if (recordingState === 'viewing' && recordedBlob && videoRef.current) {
      const url = URL.createObjectURL(recordedBlob);
      videoRef.current.srcObject = null;
      videoRef.current.src = url;
      videoRef.current.load();

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [recordingState, recordedBlob]);

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '90vh',
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
            {recordingState === 'viewing' ? (
              <>
                <PlayArrow sx={{ mr: 1 }} />
                Review Video Answer
              </>
            ) : (
              <>
                <FiberManualRecord sx={{ mr: 1 }} />
                Record Video Answer
              </>
            )}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isUploading}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.primary" sx={{ mt: 1, pr: { xs: 4, sm: 0 } }}>
          {questionText}
        </Typography>
      </DialogTitle>

      {recordingState === 'recording' && (
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              transform: 'scaleX(-1)',
              width: '100%',
              direction: 'rtl',
            }}
          >
            <LinearProgress
              variant="determinate"
              value={progress}
              color={isLowTime ? 'error' : 'primary'}
              sx={{
                height: { xs: 6, sm: 8 },
                borderRadius: 4,
                width: '100%',
              }}
            />
          </Box>
          <Typography
            variant="h4"
            color={isLowTime ? 'error.main' : 'text.secondary'}
            sx={{
              mt: 1,
              display: 'block',
              textAlign: 'center',
              fontWeight: isLowTime ? 'bold' : 'normal',
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
            }}
          >
            {timeRemaining}s remaining
          </Typography>
        </Box>
      )}

      {recordingState === 'viewing' && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Review your video answer. You can re-record if needed or submit to save.
          </Typography>
        </Box>
      )}

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            backgroundColor: 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            mb: { xs: 2, sm: 3 },
          }}
        >
          <video
            ref={videoRef}
            autoPlay={recordingState !== 'viewing'}
            muted={recordingState !== 'viewing'}
            controls={recordingState === 'viewing'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {recordingState === 'recording' && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'error.main',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem',
                fontWeight: 'medium',
              }}
            >
              <FiberManualRecord sx={{ mr: 1, fontSize: '1rem' }} />
              REC
            </Box>
          )}
        </Box>

        {recordingState === 'idle' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Camera</InputLabel>
              <Select value={selectedCamera} label="Camera" onChange={handleCameraChange}>
                {mediaDevices.cameras.map((camera) => (
                  <MenuItem key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Microphone</InputLabel>
              <Select value={selectedMicrophone} label="Microphone" onChange={handleMicrophoneChange}>
                {mediaDevices.microphones.map((microphone) => (
                  <MenuItem key={microphone.deviceId} value={microphone.deviceId}>
                    {microphone.label || `Microphone ${microphone.deviceId.slice(0, 8)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          justifyContent: 'center',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > :not(style)': { width: { xs: '100%', sm: 'auto' } },
        }}
      >
        {recordingState === 'idle' && (
          <Button
            variant="contained"
            size="large"
            startIcon={<FiberManualRecord />}
            onClick={startRecording}
            disabled={!stream || isUploading}
            sx={{ minWidth: { xs: 'auto', sm: 120 } }}
          >
            Record
          </Button>
        )}

        {recordingState === 'recording' && (
          <Button
            variant="contained"
            size="large"
            startIcon={<Stop />}
            onClick={stopRecording}
            color="error"
            disabled={!canStopRecording || isUploading}
            sx={{ minWidth: { xs: 'auto', sm: 120 } }}
          >
            {canStopRecording ? 'Stop' : `${5 - recordingDuration}s`}
          </Button>
        )}

        {recordingState === 'viewing' && (
          <>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleRerecord}
              disabled={isUploading}
              sx={{ minWidth: { xs: 'auto', sm: 120 } }}
            >
              Re-record
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <Send />}
              onClick={handleSubmit}
              disabled={isUploading}
              sx={{ minWidth: { xs: 'auto', sm: 120 } }}
            >
              {isUploading ? 'Uploading...' : 'Submit'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
